import React, { useEffect, useState } from "react";
import { Form, Container, Row, Col, Card } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  addDayActivity,
  editDayActivity,
  deleteDayActivity,
  getDayPlan,
  getoptions,
} from "../../store/slices/package";
import { getActivity } from "../../store/slices/activity";
import { toast } from "react-toastify";
import Select from "react-select";

export default function ActivityStep({
  packageId,
  activities,
  setActivities,
  nextStep,
  prevStep,
}) {
  const dispatch = useDispatch();

  const [dayPlans, setDayPlans] = useState([]);
  const [options, setOptions] = useState([]);
  const [masterActivities, setMasterActivities] = useState([]);
  const [packageActivities, setPackageActivities] = useState([]);
  const [activitySearchMap, setActivitySearchMap] = useState({});
  const [vendorsMap, setVendorsMap] = useState({});
  const [timingsMap, setTimingsMap] = useState({});
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!packageId) return;

    const fetchData = async () => {
      try {
        const item = { packageId };

        const dpRes = await dispatch(getDayPlan(item)).unwrap();
        const optRes = await dispatch(getoptions(item)).unwrap();

        const actRes = await dispatch(
          getActivity({ search: "" })
        ).unwrap();

        const dp = dpRes?.data || [];
        const opt = optRes?.data || [];
        const act = actRes?.activity || [];

        setDayPlans(dp);
        setOptions(opt);
        setMasterActivities(act);
      } catch (err) {
        toast.error("Failed to load activity data");
      }
    };

    fetchData();
  }, [packageId, dispatch]);

  useEffect(() => {
    const preloadVendorsAndTimings = () => {
      activities.forEach((config, configIndex) => {
        config.activities?.forEach((row, actIndex) => {
          if (!row.activity) return;

          const key = `${configIndex}_${actIndex}`;
          const selectedActivity = [
            ...masterActivities,
            ...packageActivities,
          ].find(
            (act) =>
              String(act._id) ===
              String(row.activity)
          );

          if (!selectedActivity?.locations) return;

          const activeLocations = selectedActivity.locations.filter(
            (loc) => loc.vendor && loc.isActive,
          );

          const vendorList = activeLocations.map((loc) => loc.vendor);

          setVendorsMap((prev) => ({
            ...prev,
            [key]: vendorList,
          }));

          if (row.vendor) {
            const location = activeLocations.find(
              (loc) => loc.vendor?._id === row.vendor,
            );

            if (location?.timings) {
              setTimingsMap((prev) => ({
                ...prev,
                [key]: location.timings,
              }));
            }
          }
        });
      });
    };

    if (activities.length && masterActivities.length) {
      preloadVendorsAndTimings();
    }
  }, [
    activities,
    masterActivities,
    packageActivities,
  ]);

  useEffect(() => {
    if (!activities?.length) return;

    const fetchPackageActivities = async () => {
      try {
        // Get all activity IDs already selected in this package
        const activityIds = [
          ...new Set(
            activities.flatMap((config) =>
              (config.activities || [])
                .map((row) => row.activity)
                .filter(Boolean)
            )
          ),
        ];

        if (!activityIds.length) {
          setPackageActivities([]);
          return;
        }

        // Fetch only the activities which are not already
        // available in the first 50 activities
        const masterIds = new Set(
          masterActivities.map((activity) =>
            String(activity._id)
          )
        );

        const missingIds = activityIds.filter(
          (id) => !masterIds.has(String(id))
        );

        if (!missingIds.length) {
          setPackageActivities([]);
          return;
        }

        const results = await Promise.all(
          missingIds.map(async (activityId) => {
            try {
              const res = await dispatch(
                getActivity({
                  id: activityId,
                })
              ).unwrap();

              // API may return object or array
              return (
                res?.activity?.[0] ||
                res?.activity ||
                res?.data?.[0] ||
                res?.data ||
                null
              );
            } catch (error) {
              console.error(
                "Failed to fetch activity:",
                activityId,
                error
              );
              return null;
            }
          })
        );

        setPackageActivities(
          results.filter(
            (activity) =>
              activity &&
              activity._id
          )
        );

        setPackageActivities(
          results.filter(Boolean)
        );
      } catch (error) {
        console.error(
          "Failed to load package activities",
          error
        );
      }
    };

    fetchPackageActivities();
  }, [activities, masterActivities, dispatch]);


  useEffect(() => {
    if (!options.length || !dayPlans.length) return;

    setActivities((prevActivities) => {
      const validOptionIds = options.map((o) => o._id);

      let filtered = prevActivities.filter((a) =>
        validOptionIds.includes(a.packageOption),
      );

      const validDays = dayPlans.slice(0, dayPlans.length - 1);

      options.forEach((option) => {
        validDays.forEach((day) => {
          const exists = filtered.find(
            (a) => a.packageOption === option._id && a.dayPlan === day._id,
          );

          if (!exists) {
            filtered.push({
              _id: null,
              packageOption: option._id,
              dayPlan: day._id,
              activities: [
                {
                  activity: "",
                  vendor: "",
                  timing: "",
                },
              ],
            });
          }
        });
      });

      return filtered;
    });
  }, [options, dayPlans]);

  const addActivityRow = (configIndex) => {
    const updated = [...activities];

    updated[configIndex].activities.push({
      activity: "",
      vendor: "",
      timing: "",
    });

    setActivities(updated);
  };

  const removeActivityRow = async (configIndex, actIndex) => {
    try {
      const updated = [...activities];
      const config = updated[configIndex];

      config.activities.splice(actIndex, 1);

      setActivities(updated);

      if (!config._id) return;

      // Delete entire day activity document if no activities remain
      if (config.activities.length === 0) {
        await dispatch(deleteDayActivity(config._id)).unwrap();

        setActivities((prev) =>
          prev.map((item, index) =>
            index === configIndex
              ? {
                ...item,
                _id: null,
                activities: [
                  {
                    activity: "",
                    vendor: "",
                    timing: "",
                  },
                ],
              }
              : item
          )
        );
      } else {
        // Otherwise update remaining activities
        await dispatch(
          editDayActivity({
            id: config._id,
            packageId,
            dayPlan: config.dayPlan,
            packageOption: config.packageOption,
            activities: config.activities,
          })
        ).unwrap();
      }

      toast.success("Activity removed");
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove activity");
    }
  };

  const handleActivitySearch = async (
    search,
    configIndex,
    actIndex
  ) => {
    try {
      const key = `${configIndex}_${actIndex}`;

      const actRes = await dispatch(
        getActivity({
          search: search || "",
        })
      ).unwrap();

      const act = actRes?.activity || actRes?.data || [];

      setActivitySearchMap((prev) => ({
        ...prev,
        [key]: act,
      }));
    } catch (err) {
      console.error("Failed to search activities", err);
    }
  };

  const handleChange = (configIndex, actIndex, field, value) => {
    const updated = [...activities];

    updated[configIndex].activities[actIndex][field] = value;

    const key = `${configIndex}_${actIndex}`;

    if (field === "activity") {
      const selectedActivity = [
        ...masterActivities,
        ...packageActivities,
        ...(activitySearchMap[key] || []),
      ].find(
        (a) => String(a._id) === String(value)
      );

      if (!selectedActivity?.locations) {
        setVendorsMap((prev) => ({
          ...prev,
          [key]: [],
        }));

        setTimingsMap((prev) => ({
          ...prev,
          [key]: [],
        }));

        setActivities(updated);
        return;
      }

      // Get active vendors
      const vendorList = selectedActivity.locations
        .filter((loc) => loc.vendor && loc.isActive)
        .map((loc) => loc.vendor);

      setVendorsMap((prev) => ({
        ...prev,
        [key]: vendorList,
      }));

      // Clear previous vendor and timing
      updated[configIndex].activities[actIndex].vendor = "";
      updated[configIndex].activities[actIndex].timing = "";

      // Clear previous timings
      setTimingsMap((prev) => ({
        ...prev,
        [key]: [],
      }));
    }
    if (field === "vendor") {
      const activityId =
        updated[configIndex].activities[actIndex]
          .activity;

      const selectedActivity = [
        ...masterActivities,
        ...packageActivities,
        ...(activitySearchMap[key] || []),
      ].find(
        (a) =>
          String(a._id) === String(activityId)
      );

      console.log(
        "Selected Activity:",
        selectedActivity
      );

      const location =
        selectedActivity?.locations?.find(
          (loc) =>
            String(loc.vendor?._id) ===
            String(value) &&
            loc.isActive
        );

      console.log(
        "Selected Location:",
        location
      );

      const timingList =
        location?.timings || [];

      console.log(
        "Timing List:",
        timingList
      );

      setTimingsMap((prev) => ({
        ...prev,
        [key]: timingList,
      }));

      updated[configIndex].activities[actIndex].timing =
        "";
    }

    setActivities(updated);
  };


  const copyToAllOptions = (sourceConfig) => {
    setActivities((prev) => {
      return prev.map((config) => {

        // same day, different option
        if (
          String(config.dayPlan) ===
          String(sourceConfig.dayPlan) &&
          String(config.packageOption) !==
          String(sourceConfig.packageOption)
        ) {
          return {
            ...config,
            activities: sourceConfig.activities.map((act) => ({
              ...act,
            })),
          };
        }

        return config;
      });
    });

    toast.success("Copied to all options");
  };

  const handleSubmit = async () => {
    try {
      for (const config of activities) {
        const payload = {
          packageId,
          dayPlan: config.dayPlan,
          packageOption: config.packageOption,
          activities: config.activities.filter((a) => a.activity),
        };

        if (config._id) {
          await dispatch(
            editDayActivity({
              id: config._id,
              ...payload,
            }),
          ).unwrap();
        } else {
          const res = await dispatch(addDayActivity(payload)).unwrap();
          config._id = res?._id || res?.data?._id || null;
        }
      }

      toast.success("Activities saved successfully");
      nextStep();
    } catch (err) {
      toast.error("Failed to save activities");
    }
  };

  const activitiesByOption = options.map((option) => ({
    option,
    configs: activities.filter((a) => a.packageOption === option._id),
  }));

  const getActivityOptions = (configIndex, actIndex) => {
    const searchedActivities =
      activitySearchMap[
      `${configIndex}_${actIndex}`
      ] || [];

    const allActivities = [
      ...masterActivities,
      ...packageActivities,
      ...searchedActivities,
    ];

    // Remove duplicate activities
    return allActivities.filter(
      (activity, index, self) =>
        index ===
        self.findIndex(
          (item) =>
            String(item._id) === String(activity._id)
        )
    );
  };

  return (
    <Container>
      <Card className="p-4 shadow-sm border-0 mt-2">
        <Row>
          {activitiesByOption.map(({ option, configs }) => (
            <Col md={6} key={option._id}>
              <Card className="p-3 mb-3 shadow-sm">
                <h5 className="text-center mb-3">{option.name}</h5>
                {configs.map((config) => {
                  const configIndex = activities.findIndex(
                    (a) =>
                      a.packageOption === config.packageOption &&
                      a.dayPlan === config.dayPlan,
                  );

                  const day = dayPlans.find((d) => d._id === config.dayPlan);

                  return (
                    <Card key={configIndex} className="mb-3 p-3 border">
                      <h6>Day {day?.dayNumber}</h6>

                      {config.activities.map((actRow, actIndex) => (
                        <Row key={actIndex} className="g-3 mb-2">
                          <Col md={4}>
                            <Form.Label>Activity</Form.Label>
                            <Select
                              placeholder="Search Activity"
                              isSearchable
                              value={
                                actRow.activity
                                  ? (() => {
                                    const selectedActivity =
                                      getActivityOptions(
                                        configIndex,
                                        actIndex
                                      ).find(
                                        (act) =>
                                          String(act._id) ===
                                          String(actRow.activity)
                                      );

                                    return {
                                      value: actRow.activity,
                                      label:
                                        selectedActivity?.name ||
                                        "Selected Activity",
                                    };
                                  })()
                                  : null
                              }
                              onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                  handleActivitySearch(
                                    value,
                                    configIndex,
                                    actIndex
                                  );
                                }
                              }}
                              onChange={(selected) =>
                                handleChange(
                                  configIndex,
                                  actIndex,
                                  "activity",
                                  selected?.value || ""
                                )
                              }
                              options={getActivityOptions(
                                configIndex,
                                actIndex
                              ).map((act) => ({
                                value: act._id,
                                label: act.name,
                              }))}
                            />
                          </Col>

                          {/* Vendor */}
                          <Col md={4}>
                            <Form.Label>Vendor</Form.Label>
                            <Select
                              placeholder="Select Vendor"
                              isDisabled={!actRow.activity}
                              value={
                                (
                                  vendorsMap[`${configIndex}_${actIndex}`] || []
                                )
                                  .map((ven) => ({
                                    value: ven._id,
                                    label: ven.name,
                                  }))
                                  .find((o) => o.value === actRow.vendor) || null
                              }
                              onChange={(selected) =>
                                handleChange(
                                  configIndex,
                                  actIndex,
                                  "vendor",
                                  selected?.value || "",
                                )
                              }
                              options={(
                                vendorsMap[`${configIndex}_${actIndex}`] || []
                              ).map((ven) => ({
                                value: ven._id,
                                label: ven.name,
                              }))}
                            />
                          </Col>

                          {/* Timing */}
                          <Col md={3}>
                            <Form.Label>Timing</Form.Label>

                            <Form.Select
                              value={actRow.timing || ""}
                              onChange={(e) =>
                                handleChange(
                                  configIndex,
                                  actIndex,
                                  "timing",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">Select Timing</option>

                              {(
                                timingsMap[`${configIndex}_${actIndex}`] || []
                              ).map((time, i) => (
                                <option key={i} value={time}>
                                  {time}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>

                          {/* Remove Button */}
                          <Col md={1} className="d-flex align-items-end">
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() =>
                                removeActivityRow(configIndex, actIndex)
                              }
                            >
                              ✕
                            </button>
                          </Col>
                        </Row>
                      ))}
                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="cancel-btn btn-sm"
                          onClick={() => addActivityRow(configIndex)}
                        >
                          + Add Activity
                        </button>

                        <button
                          type="button"
                          className="cancel-btn btn-sm"
                          onClick={() => copyToAllOptions(config)}
                        >
                          Copy To All
                        </button>
                      </div>
                    </Card>
                  );
                })}
              </Card>
            </Col>
          ))}
        </Row>
        <div className="d-flex justify-content-between mt-4 mb-5">
          <button className="cancel-btn" onClick={prevStep}>
            Previous
          </button>

          <button className="submit-btn" onClick={handleSubmit}>
            Next
          </button>
        </div>
      </Card>
    </Container>
  );
}